import React from 'react';

/** @typedef {import('./FormDesigner.jsx').DesignerFieldStateObj} DesignerFieldStateObj */
/** @typedef {import('../transformers/FormFieldTransformer.js').DesignerFieldObj} DesignerFieldObj */
/** @typedef {import('../enums.js').FormFieldTypeObj} FormFieldTypeObj */

/**
 * @typedef {object} FormDesignerFieldEditorProps
 * @property {DesignerFieldStateObj} field - Champ en cours d'édition
 * @property {Array<FormFieldTypeObj>} types - Types de champ disponibles
 * @property {boolean} disabled - Si l'édition est désactivée (enregistrement en cours)
 * @property {(key: string, property: keyof DesignerFieldObj, value: any) => void} onChange - Modification d'une propriété du champ
 */

/**
 * Panneau d'édition des propriétés d'un champ du designer de formulaire
 * NOTE seules les propriétés utiles au type sélectionné sont affichées, les autres sont conservées telles quelles
 * @param {FormDesignerFieldEditorProps} props
 */
export default function FormDesignerFieldEditor({ field, types, disabled, onChange }) {
  const type = types.find(item => item.value === field.type);
  const idPrefix = `form-designer-${ field.key }`;

  /**
   * Modification d'un texte (null si vide)
   * @param {keyof DesignerFieldObj} property - Propriété à modifier
   * @returns {(event: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>) => void}
   */
  const changeText = property => event => onChange(field.key, property, event.target.value === '' && property !== 'label' ? null : event.target.value);

  /**
   * Modification d'un booléen depuis une liste Oui / Non
   * @param {keyof DesignerFieldObj} property - Propriété à modifier
   * @returns {(event: React.ChangeEvent<HTMLSelectElement>) => void}
   */
  const changeBoolean = property => event => onChange(field.key, property, event.target.value === 'true');

  /**
   * Liste Oui / Non pour une propriété booléenne
   * @param {keyof DesignerFieldObj} property - Propriété
   * @param {string} label - Libellé du champ
   * @param {string} [help] - Texte d'aide
   * @param {[string, string]} [labels] - Libellés des valeurs vrai / faux
   */
  const booleanSelect = (property, label, help, labels = [ 'Oui', 'Non' ]) => (
    <div className="input-group input-group--no-margin">
      <label htmlFor={`${ idPrefix }-${ property }`} className="input-group__label">{label}</label>
      <select id={`${ idPrefix }-${ property }`} className="input-group__input" value={String(Boolean(field[property]))} disabled={disabled} onChange={changeBoolean(property)}>
        <option value="true">{labels[0]}</option>
        <option value="false">{labels[1]}</option>
      </select>
      {help && <div className="input-group__help">{help}</div>}
    </div>
  );

  return (
    <div className="uiblock uiblock--no-header" style={{ height: '100%', minHeight: 0 }}>
      <div className="uiblock__content" style={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', gap: '16px', height: '100%', minHeight: 0, boxSizing: 'border-box' }}>
        <h4 className="title">Propriétés du champ</h4>

        <div style={{ overflowY: 'auto', minHeight: 0 }}>
          <div className="input-group input-group--no-margin">
            <label htmlFor={`${ idPrefix }-label`} className="input-group__label">* Libellé</label>
            <input id={`${ idPrefix }-label`} className="input-group__input" type="text" maxLength={255} value={field.label} placeholder="Ex : Votre adresse e-mail" disabled={disabled} onChange={changeText('label')} />
            {type?.isImage && <div className="input-group__help">Le libellé d'une image d'aide n'est pas affiché sur le formulaire.</div>}
          </div>
          <div className="space--16"></div>

          <div className="input-group input-group--no-margin">
            <label htmlFor={`${ idPrefix }-type`} className="input-group__label">* Type</label>
            <select id={`${ idPrefix }-type`} className="input-group__input" value={field.type} disabled={disabled} onChange={changeText('type')}>
              {types.map(item => <option key={item.value} value={item.value}>{item.name}</option>)}
            </select>
          </div>
          <div className="space--16"></div>

          {type?.hasList && (
            <>
              <div className="input-group input-group--no-margin">
                <label htmlFor={`${ idPrefix }-list`} className="input-group__label">* Liste de valeurs</label>
                <input id={`${ idPrefix }-list`} className="input-group__input" type="text" maxLength={255} value={field.list || ''} placeholder="Ex : Valeur 1;Valeur 2;Valeur 3" disabled={disabled} onChange={changeText('list')} />
                <div className="input-group__help">Valeurs séparées par des « ; »</div>
              </div>
              <div className="space--16"></div>
            </>
          )}

          {type?.hasPlaceholder && (
            <>
              <div className="input-group input-group--no-margin">
                <label htmlFor={`${ idPrefix }-placeholder`} className="input-group__label">{type.isImage ? '* Adresse de l\'image' : 'Texte de remplacement'}</label>
                <input id={`${ idPrefix }-placeholder`} className="input-group__input" type="text" maxLength={255} value={field.placeholder || ''} placeholder={type.isImage ? 'Ex : /uploads/mon-image.jpg' : 'Ex : nom@exemple.fr'} disabled={disabled} onChange={changeText('placeholder')} />
                <div className="input-group__help">{type.isImage ? 'Adresse de l\'image affichée dans le formulaire' : 'Texte affiché en filigrane dans le champ vide'}</div>
              </div>
              {type.isImage && field.placeholder && (
                <>
                  <div className="space--8"></div>
                  <img src={field.placeholder} alt="" style={{ maxWidth: '100%', maxHeight: '150px' }} />
                </>
              )}
              <div className="space--16"></div>
            </>
          )}

          {type?.isInput && (
            <>
              <div className="input-group input-group--no-margin">
                <label htmlFor={`${ idPrefix }-help`} className="input-group__label">Texte d'aide</label>
                <input id={`${ idPrefix }-help`} className="input-group__input" type="text" maxLength={255} value={field.help || ''} placeholder="Ex : Nous vous répondrons à cette adresse" disabled={disabled} onChange={changeText('help')} />
                <div className="input-group__help">Texte d'explication affiché sous le champ</div>
              </div>
              <div className="space--16"></div>
              {booleanSelect('required', 'Requis', 'Le visiteur doit obligatoirement remplir ce champ')}
              <div className="space--16"></div>
            </>
          )}

          {type?.hasOtherResponse && (
            <>
              {booleanSelect('has_other_response', 'Autre réponse', 'Ajoute un choix « Autre » avec une réponse libre')}
              <div className="space--16"></div>
            </>
          )}

          {booleanSelect('go_to_next_line', 'Passer à la ligne après ce champ', 'Sinon, le champ suivant s\'affiche sur la même ligne (3 champs maximum par ligne)')}
          <div className="space--16"></div>

          {booleanSelect('state', 'État', null, [ 'Actif', 'Inactif' ])}
        </div>
      </div>
    </div>
  );
}
